import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { ApiClient } from './api-client';
import { Endpoints } from './endpoints';
import { CreateMemberRequest } from '../models/users/requests/create-member-request';
import { CreateNonMemberRequest } from '../models/users/requests/create-non-member-request';
import { Observable, of, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, map } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { MemberUser } from '../models/users/dto/member-user';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { ChangeEmailRequest } from '../models/users/requests/change-email-request';
import { HttpResponse } from '@angular/common/http';
import { ODataResponse } from '../models/protocols/odata-response';
import { MemberUserSearchResult } from '../models/users/dto/member-user-search-result';
import { MemberRecordCommentRequest } from '../models/users/requests/member-record-comment-request';
import { MemberRecordComment } from '../views/records/components/members/ip-member-record/member-record-comments/member-record-comment';
import { Mentionable } from '../models/base/mentionable';
import { InternalUserCommentReadReceiptRequest } from '../models/users/requests/internal-user-comment-read-receipt-request';
import { EmploymentHistory } from '../models/users/dto/employment-history';
import { EmployeeTableUser } from '../models/users/dto/employee-table-user';
import { MostRecentInvite } from '../models/users/dto/most-recent-invite';
import { ResendMemberInviteRequest } from '../models/users/requests/resend-member-invite-request';
import { MemberRecord } from '../models/users/dto/member-record';
import { MemberSpouseContactInfoRequest } from '../models/users/requests/member-spouse-contact-info-request';
import { MemberRelationshipStatus } from '../models/users/dto/member-relationship-status';
import { IpMemberSpouseContactInfoRequest } from '../models/users/requests/ip-member-spouse-contact-info-request';
import { MemberPersonalInformationOnboardingRequest } from '../models/users/requests/member-personal-information-onboarding-request';
import { MemberOnboardingProgress } from '../models/members/member-onboarding-progress';
import { NewMemberVerificationRequest } from '../models/members/new-member-verification-request';
import { NewMemberVerificationResponse } from '../models/members/new-member-verification-response';
import { VariableBenefitPaymentEstimate } from '../models/submissions/variable-benefit-payment-estimate';
import { VariableBenefitTargetPayment } from '../models/submissions/variable-benefit-target-payment';
import {
  MpVariableBenefitTargetPaymentRequest,
  VariableBenefitTargetPaymentRequest
} from '../models/submissions/requests/variable-benefit-target-payment-request';
import {
  MpVariableBenefitPaymentEstimateRequest,
  VariableBenefitPaymentEstimateRequest
} from '../models/submissions/requests/variable-benefit-payment-estimate-request';
import { MemberPlanRules } from '../models/users/dto/member-plan-rules';
import { VariableBenefitSummary } from '../models/submissions/variable-benefit-summary';
import { MemberRetirementStub } from '../models/retirement/member-retirement-stub';
import { MemberRetirementType } from '../models/retirement/member-retirement-type';
import { MemberRetirementStubUpdateRequest } from '../models/retirement/member-retirement-stub-update-request';
import { MemberLifeStatus } from '../models/users/dto/member-life-status';
import { MemberPaymentSuspension } from '../models/users/dto/member-payment-suspension';
import { MemberLifeStatusUpdateRequest } from '../models/users/requests/member-life-status-update-request';
import { MemberDeathRecord } from '../models/users/dto/member-death-record';
import { MemberDeathRecordSettlementRequest } from '../models/users/dto/member-death-record-settlement';
import { MemberDeathRecordAttachment } from '../models/users/dto/member-death-record-attachment';
import {
  MemberDeathRecordComment,
  MemberDeathRecordCommentRequest
} from '../models/users/dto/member-death-record-comment';
import { CreateAttachmentRequest } from '../models/users/requests/create-attachment-request';
import { AddressFormGroup } from '../forms/address-form-group';

@Injectable({
  providedIn: 'root'
})
export class MembersAPI implements LoggableAPI {
  private apiClient = inject(ApiClient);

  constructor() {}

  public serviceName = 'Members';

  public ipCreateMember(req: CreateMemberRequest): Observable<MemberUser> {
    const url = Endpoints.ipCreateMember();
    return this.apiClient.postObj(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateNonMember(req: CreateNonMemberRequest): Observable<MemberUser> {
    const url = Endpoints.ipCreateNonMember();
    return this.apiClient.postObj(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberRetirementStub(memberId: number): Observable<MemberRetirementStub | null> {
    const url = Endpoints.ipGetMemberRetirementStub(memberId);
    return this.apiClient.getObj<MemberRetirementStub>(MemberRetirementStub, url).pipe(
      defaultIfEmpty(null),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberRetirementStub(request: MemberRetirementStubUpdateRequest): Observable<MemberRetirementStub> {
    const url = Endpoints.ipUpdateMemberRetirementStub(request.memberId, request.id);
    return this.apiClient.putObj<MemberRetirementStub>(MemberRetirementStub, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMemberRetirementType(): Observable<MemberRetirementType | null> {
    const url = Endpoints.mpGetMemberRetirementType();
    return this.apiClient.getObj<MemberRetirementType>(MemberRetirementType, url).pipe(
      defaultIfEmpty(null),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberPaymentSuspension(memberId: number): Observable<MemberPaymentSuspension> {
    const url = Endpoints.ipGetMemberPaymentSuspension(memberId);
    return this.apiClient.getObj<MemberPaymentSuspension>(MemberPaymentSuspension, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSuspendMemberPayments(memberId: number): Observable<MemberPaymentSuspension> {
    const url = Endpoints.ipSuspendMemberPayments(memberId);
    return this.apiClient.putObj<MemberPaymentSuspension>(MemberPaymentSuspension, url, { memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipResumeMemberPayments(memberId: number): Observable<MemberPaymentSuspension> {
    const url = Endpoints.ipResumeMemberPayments(memberId);
    return this.apiClient.putObj<MemberPaymentSuspension>(MemberPaymentSuspension, url, { memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberLifeStatus(memberId: number): Observable<MemberLifeStatus> {
    const url = Endpoints.ipGetMemberLifeStatus(memberId);
    return this.apiClient.getObj<MemberLifeStatus>(MemberLifeStatus, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberLifeStatus(request: MemberLifeStatusUpdateRequest): Observable<MemberLifeStatus> {
    const url = Endpoints.ipUpdateMemberLifeStatus(request.memberId);
    return this.apiClient.putObj<MemberLifeStatus>(MemberLifeStatus, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  /** Emits null when the member has no death record, which the backend reports as a 404. */
  public ipGetMemberDeathRecord(memberId: number): Observable<MemberDeathRecord | null> {
    const url = Endpoints.ipGetMemberDeathRecord(memberId);
    return this.apiClient.getObj<MemberDeathRecord>(MemberDeathRecord, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return err.code === 404 ? of(null) : throwError(() => err);
      })
    );
  }

  public ipCreateMemberDeathRecord(memberId: number): Observable<MemberDeathRecord> {
    const url = Endpoints.ipCreateMemberDeathRecord(memberId);
    return this.apiClient.postObj<MemberDeathRecord>(MemberDeathRecord, url, { memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateDeathRecordAdminDetails(
    memberId: number,
    deathRecordId: number,
    adminDetails: Pick<MemberDeathRecord, 'deathDate' | 'notificationDate' | 'contactMethodId'>
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipUpdateDeathRecordAdminDetails(memberId, deathRecordId);
    return this.apiClient
      .putObj<MemberDeathRecord>(MemberDeathRecord, url, { memberId, id: deathRecordId, ...adminDetails })
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipCloseDeathRecord(memberId: number, deathRecordId: number): Observable<MemberDeathRecord> {
    const url = Endpoints.ipCloseDeathRecord(memberId, deathRecordId);
    return this.apiClient.putObj<MemberDeathRecord>(MemberDeathRecord, url, { memberId, id: deathRecordId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipReopenDeathRecord(memberId: number, deathRecordId: number, reason: string): Observable<MemberDeathRecord> {
    const url = Endpoints.ipReopenDeathRecord(memberId, deathRecordId);
    return this.apiClient
      .putObj<MemberDeathRecord>(MemberDeathRecord, url, { memberId, id: deathRecordId, reason })
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipArchiveDeathRecord(memberId: number, deathRecordId: number): Observable<unknown> {
    const url = Endpoints.ipArchiveDeathRecord(memberId, deathRecordId);
    return this.apiClient.putWithUntypedRes(url, { memberId, id: deathRecordId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateDeathRecordReminder(
    memberId: number,
    deathRecordId: number,
    date: string
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipCreateDeathRecordReminder(memberId, deathRecordId);
    return this.apiClient.postObj<MemberDeathRecord>(MemberDeathRecord, url, { memberId, deathRecordId, date }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteDeathRecordReminder(
    memberId: number,
    deathRecordId: number,
    reminderId: number
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipDeleteDeathRecordReminder(memberId, deathRecordId, reminderId);
    return this.apiClient.deleteObj<MemberDeathRecord>(MemberDeathRecord, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSaveDeathRecordNotifyingParty(
    memberId: number,
    deathRecordId: number,
    notifyingParty: {
      firstName: string | null;
      lastName: string | null;
      email: string | null;
      phoneNumberCountryCode: string;
      phoneNumber: string | null;
      address: ReturnType<AddressFormGroup['getRawValue']>;
    },
    exists: boolean
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipDeathRecordNotifyingParty(memberId, deathRecordId);
    const request = { memberId, deathRecordId, ...notifyingParty };
    const save$ = exists
      ? this.apiClient.putObj<MemberDeathRecord>(MemberDeathRecord, url, request)
      : this.apiClient.postObj<MemberDeathRecord>(MemberDeathRecord, url, request);
    return save$.pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSaveDeathRecordLegalRepresentative(
    memberId: number,
    deathRecordId: number,
    legalRepresentative: {
      lawFirm: string | null;
      contactFirstName: string | null;
      contactLastName: string | null;
      email: string | null;
      phoneNumberCountryCode: string;
      phoneNumber: string | null;
      address: ReturnType<AddressFormGroup['getRawValue']>;
    },
    exists: boolean
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipDeathRecordLegalRepresentative(memberId, deathRecordId);
    const request = { memberId, deathRecordId, ...legalRepresentative };
    const save$ = exists
      ? this.apiClient.putObj<MemberDeathRecord>(MemberDeathRecord, url, request)
      : this.apiClient.postObj<MemberDeathRecord>(MemberDeathRecord, url, request);
    return save$.pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteDeathRecordNotifyingParty(memberId: number, deathRecordId: number): Observable<MemberDeathRecord> {
    const url = Endpoints.ipDeathRecordNotifyingParty(memberId, deathRecordId);
    return this.apiClient.deleteObj<MemberDeathRecord>(MemberDeathRecord, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteDeathRecordLegalRepresentative(
    memberId: number,
    deathRecordId: number
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipDeathRecordLegalRepresentative(memberId, deathRecordId);
    return this.apiClient.deleteObj<MemberDeathRecord>(MemberDeathRecord, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipAddDeathRecordSettlement(
    memberId: number,
    deathRecordId: number,
    deathRecordAccountId: number,
    settlement: MemberDeathRecordSettlementRequest
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipAddDeathRecordSettlement(memberId, deathRecordId, deathRecordAccountId);
    return this.apiClient
      .postObj<MemberDeathRecord>(MemberDeathRecord, url, {
        memberId,
        deathRecordId,
        deathRecordAccountId,
        ...settlement
      })
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipUpdateDeathRecordSettlement(
    memberId: number,
    deathRecordId: number,
    settlementId: number,
    settlement: MemberDeathRecordSettlementRequest
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipDeathRecordSettlement(memberId, deathRecordId, settlementId);
    return this.apiClient
      .putObj<MemberDeathRecord>(MemberDeathRecord, url, { memberId, deathRecordId, id: settlementId, ...settlement })
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipReleaseDeathRecordSettlement(
    memberId: number,
    deathRecordId: number,
    settlementId: number,
    isReleased: boolean
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipReleaseDeathRecordSettlement(memberId, deathRecordId, settlementId);
    return this.apiClient
      .putObj<MemberDeathRecord>(MemberDeathRecord, url, { memberId, deathRecordId, id: settlementId, isReleased })
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipDeleteDeathRecordSettlement(
    memberId: number,
    deathRecordId: number,
    settlementId: number
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipDeathRecordSettlement(memberId, deathRecordId, settlementId);
    return this.apiClient.deleteObj<MemberDeathRecord>(MemberDeathRecord, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateDeathRecordAttachment(
    memberId: number,
    deathRecordId: number,
    attachment: CreateAttachmentRequest
  ): Observable<MemberDeathRecordAttachment> {
    const url = Endpoints.ipCreateDeathRecordAttachment(memberId, deathRecordId);
    return this.apiClient
      .postObj<MemberDeathRecordAttachment>(MemberDeathRecordAttachment, url, { memberId, deathRecordId, attachment })
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipDeleteDeathRecordAttachment(
    memberId: number,
    deathRecordId: number,
    attachmentId: string
  ): Observable<MemberDeathRecord> {
    const url = Endpoints.ipDeleteDeathRecordAttachment(memberId, deathRecordId, attachmentId);
    return this.apiClient.deleteObj<MemberDeathRecord>(MemberDeathRecord, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetDeathRecordComments(deathRecordId: number): Observable<ODataResponse<MemberDeathRecordComment>> {
    const url = Endpoints.ipGetDeathRecordComments(deathRecordId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('mentions');
    return this.apiClient.getOdata<MemberDeathRecordComment>(url, MemberDeathRecordComment, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipAddDeathRecordComment(req: MemberDeathRecordCommentRequest): Observable<MemberDeathRecordComment[]> {
    const url = Endpoints.ipAddDeathRecordComment(req.deathRecordId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => window.injector.Deserialize.arrayOf(MemberDeathRecordComment, res.body)),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSetDeathRecordCommentsAsRead(req: InternalUserCommentReadReceiptRequest): Observable<HttpResponse<any>> {
    const url = Endpoints.ipCreateDeathRecordCommentReadReceipts();
    return this.apiClient.simplePost(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateMemberOnboardingInformation(req: MemberPersonalInformationOnboardingRequest): Observable<MemberUser> {
    const url = Endpoints.updateMemberOnboardingInformation();
    return this.apiClient.putObj<MemberUser>(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberCommentMentionables(opts: ODataQueryOptions): Observable<ODataResponse<Mentionable>> {
    const url = Endpoints.ipGetMemberCommentMentionables();
    opts.setExpand('ProfileBadgeConfiguration');
    return this.apiClient.getOdata<Mentionable>(url, Mentionable, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMember(odataQueryOptions: ODataQueryOptions): Observable<MemberUser> {
    const url = Endpoints.mpGetMember();
    return this.apiClient.getOdataObj<MemberUser>(url, MemberUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberById(memberId: number, odataQueryOptions = new ODataQueryOptions()): Observable<MemberRecord> {
    const url = Endpoints.internalPortalGetMemberById(memberId);
    return this.apiClient.getOdataObj<MemberRecord>(url, MemberRecord, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipRestrictMemberPortalAccess(memberId: number): Observable<MemberRecord> {
    const url = Endpoints.ipRestrictMemberPortalAccess(memberId);
    return this.apiClient.putObj<MemberRecord>(MemberRecord, url, { memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipRestoreMemberPortalAccess(memberId: number): Observable<MemberRecord> {
    const url = Endpoints.ipRestoreMemberPortalAccess(memberId);
    return this.apiClient.putObj<MemberRecord>(MemberRecord, url, { memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetMemberById(memberId: number, odataQueryOptions: ODataQueryOptions): Observable<MemberUser> {
    const url = Endpoints.epGetMemberById(memberId);
    return this.apiClient.getOdataObj<MemberUser>(url, MemberUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerMembers(odataQueryOptions: ODataQueryOptions): Observable<ODataResponse<EmployeeTableUser>> {
    const url = Endpoints.epGetEmployerMembers();
    return this.apiClient.getOdata<EmployeeTableUser>(url, EmployeeTableUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getAllMembers(odataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberUser>> {
    const url = Endpoints.getAllInternalMembers();
    return this.apiClient.getOdata<MemberUser>(url, MemberUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public forceChangeEmail(memberId: number, req: ChangeEmailRequest): Observable<MemberUser> {
    const url = Endpoints.memberForceChangeEmail(memberId);
    return this.apiClient.postObj(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public forceChangePassword(memberId: string): Observable<HttpResponse<any>> {
    const url = Endpoints.memberForceChangePassword(memberId);
    return this.apiClient.simpleBodylessPost(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSearchMembersByIdOrName(
    odataQueryOptions: ODataQueryOptions,
    employerId?: number
  ): Observable<ODataResponse<MemberUserSearchResult>> {
    const url = Endpoints.searchInternalMembersByIdOrName();
    const additionalParams = employerId !== undefined ? 'employerId=' + employerId?.toString() : '';
    return this.apiClient
      .getOdata<MemberUserSearchResult>(url, MemberUserSearchResult, odataQueryOptions, null, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public epSearchMembersByIdOrName(
    odataQueryOptions: ODataQueryOptions,
    employerId?: number
  ): Observable<ODataResponse<MemberUserSearchResult>> {
    const url = Endpoints.epSearchMembersByIdOrName();
    const additionalParams = employerId !== undefined ? 'employerId=' + employerId?.toString() : '';
    return this.apiClient
      .getOdata<MemberUserSearchResult>(url, MemberUserSearchResult, odataQueryOptions, null, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public addCommentToMemberRecord(req: MemberRecordCommentRequest): Observable<MemberRecordComment[]> {
    const url = Endpoints.memberComments(req.memberId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => {
        const comments = window?.injector?.Deserialize?.arrayOf(MemberRecordComment, res.body);
        return comments.sort((a, b) => {
          // @ts-ignore
          return new Date(b.createdDate) - new Date(a.createdDate);
        });
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getInternalMemberEmploymentHistory(memberId: number): Observable<EmploymentHistory[]> {
    const odataQueryOptions = new ODataQueryOptions();
    odataQueryOptions.setExpand(
      'DesignatedUnit($expand=DesignatedUnitType)',
      'GoverningJurisdiction',
      'ProvinceOfEmployment',
      'EmploymentType',
      'EmploymentStatus',
      'LeaveDetails',
      'StatusChangeReason'
    );
    const url = Endpoints.getInternalMemberEmploymentHistory(memberId);
    return this.apiClient.getOdata<EmploymentHistory>(url, EmploymentHistory, odataQueryOptions).pipe(
      map(res => res?.value),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerMemberEmploymentHistory(memberId: number): Observable<EmploymentHistory[]> {
    const odataQueryOptions = new ODataQueryOptions();
    odataQueryOptions.setExpand(
      'DesignatedUnit($expand=DesignatedUnitType)',
      'GoverningJurisdiction',
      'ProvinceOfEmployment',
      'EmploymentType',
      'EmploymentStatus',
      'LeaveDetails'
    );
    const url = Endpoints.getEmployerMemberEmploymentHistory(memberId);
    return this.apiClient.getOdata<EmploymentHistory>(url, EmploymentHistory, odataQueryOptions).pipe(
      map(res => res?.value),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberEmploymentHistory(): Observable<EmploymentHistory[]> {
    const odataQueryOptions = new ODataQueryOptions();
    odataQueryOptions.setExpand('ProvinceOfEmployment', 'EmploymentType', 'EmploymentStatus', 'LeaveDetails');
    const url = Endpoints.getMemberEmploymentHistory();
    return this.apiClient.getOdata<EmploymentHistory>(url, EmploymentHistory, odataQueryOptions).pipe(
      map(res => res?.value),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createInternalMemberEmploymentHistory(
    req: EmploymentHistory,
    memberId: number
  ): Observable<EmploymentHistory> {
    const url = Endpoints.createInternalMemberEmploymentHistory(memberId);
    return this.apiClient.postObj(EmploymentHistory, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateInternalMemberEmploymentHistory(req: EmploymentHistory): Observable<EmploymentHistory> {
    const url = Endpoints.updateInternalMemberEmploymentHistory(req.memberId, req.id);
    return this.apiClient.putObj(EmploymentHistory, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateEmployerMemberEmploymentHistory(req: EmploymentHistory): Observable<EmploymentHistory> {
    const url = Endpoints.updateEmployerMemberEmploymentHistory(req.memberId, req.id);
    return this.apiClient.putObj(EmploymentHistory, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public resendMemberInvite(memberId: string, req: ResendMemberInviteRequest): Observable<MostRecentInvite> {
    const url = Endpoints.resendMemberInvite(memberId);
    return this.apiClient.postObj(MostRecentInvite, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getCommentsForMember(memberId: string): Observable<ODataResponse<MemberRecordComment>> {
    const url = Endpoints.getCommentsForMember(memberId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('mentions');
    return this.apiClient.getOdata<MemberRecordComment>(url, MemberRecordComment, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpUpdateMemberSpouseContactInfo(req: MemberSpouseContactInfoRequest): Observable<MemberRelationshipStatus> {
    const url = Endpoints.mpUpdateMemberSpouseContactInfo(req.id);
    return this.apiClient.putObj(MemberRelationshipStatus, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberSpouseContactInfo(req: IpMemberSpouseContactInfoRequest): Observable<MemberRelationshipStatus> {
    const url = Endpoints.ipUpdateMemberSpouseContactInfo(req.memberId, req.spouseId);
    return this.apiClient.putObj(MemberRelationshipStatus, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMemberOnboardingProgress(opts: ODataQueryOptions): Observable<MemberOnboardingProgress> {
    const url = Endpoints.getMemberOnboardingProgress();
    return this.apiClient.getOdataObj<MemberOnboardingProgress>(url, MemberOnboardingProgress, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetNewMembersToVerify(req: NewMemberVerificationRequest[]): Observable<NewMemberVerificationResponse[]> {
    const url = Endpoints.ipGetNewMembersToVerify();
    return this.apiClient.postArr(NewMemberVerificationResponse, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateNewMembers(req: CreateMemberRequest[]): Observable<MemberUser[]> {
    const url = Endpoints.ipCreateMembers();
    return this.apiClient.postArr(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSetMemberRecordCommentsAsRead(req: InternalUserCommentReadReceiptRequest): Observable<HttpResponse<any>> {
    const url = Endpoints.ipCreateMemberRecordReadReceipts();
    return this.apiClient.simplePost(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epCreateEmployees(req: CreateMemberRequest[]): Observable<HttpResponse<any>> {
    const url = Endpoints.epCreateEmployees();
    return this.apiClient.postWithUntypedRes(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetVariableBenefitPaymentEstimate(
    memberId: number,
    req: VariableBenefitPaymentEstimateRequest
  ): Observable<VariableBenefitPaymentEstimate> {
    const url = Endpoints.ipGetVariableBenefitPaymentEstimate(memberId);
    return this.apiClient.postObj(VariableBenefitPaymentEstimate, url, { ...req, memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetVariableBenefitPaymentEstimate(
    req: MpVariableBenefitPaymentEstimateRequest
  ): Observable<VariableBenefitPaymentEstimate> {
    const url = Endpoints.mpGetVariableBenefitPaymentEstimate();
    return this.apiClient.postObj(VariableBenefitPaymentEstimate, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetVariableBenefitTargetPayment(
    memberId: number,
    req: VariableBenefitTargetPaymentRequest
  ): Observable<VariableBenefitTargetPayment> {
    const url = Endpoints.ipGetVariableBenefitTargetPayment(memberId);
    return this.apiClient.postObj(VariableBenefitTargetPayment, url, { ...req, memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetVariableBenefitTargetPayment(
    req: MpVariableBenefitTargetPaymentRequest
  ): Observable<VariableBenefitTargetPayment> {
    const url = Endpoints.mpGetVariableBenefitTargetPayment();
    return this.apiClient.postObj(VariableBenefitTargetPayment, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberVariableBenefitSummary(memberId: number): Observable<VariableBenefitSummary> {
    const url = Endpoints.ipGetMemberVariableBenefitSummary(memberId);
    return this.apiClient.getObj(VariableBenefitSummary, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMemberVariableBenefitSummary(): Observable<VariableBenefitSummary> {
    const url = Endpoints.mpGetVariableBenefitSummary();
    return this.apiClient.getObj(VariableBenefitSummary, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberPlanRules(memberId: number): Observable<MemberPlanRules> {
    const url = Endpoints.ipGetMemberPlanRules(memberId);
    return this.apiClient.getObj(MemberPlanRules, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
